/**
 * AwsAssignment.jsx
 * -----------------
 * Study page for the "AWS Cloud Computing - Basic Assignment":
 * 8 modules, 80 questions, model answers, comparison tables and diagrams.
 *
 * Self-contained: no dependencies, no CSS framework, no font loading.
 * Every class is prefixed `aws-` and scoped under `.aws-root`.
 */

import React, { useState, useMemo, useEffect, useCallback } from "react";

/* =========================================================================
   Diagram primitives
   ========================================================================= */

function Box({ x, y, w, h, title, sub, tone }) {
  const cls =
    tone === "accent" ? "aws-svg-accent" : tone === "muted" ? "aws-svg-muted" : "aws-svg-box";
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="4" className={cls} />
      <text x={x + w / 2} y={y + (sub ? h / 2 - 3 : h / 2 + 4)} textAnchor="middle" className="aws-svg-t">
        {title}
      </text>
      {sub && (
        <text x={x + w / 2} y={y + h / 2 + 12} textAnchor="middle" className="aws-svg-t2">
          {sub}
        </text>
      )}
    </g>
  );
}

function Arrow({ x1, y1, x2, y2, dashed }) {
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      className="aws-svg-line"
      strokeDasharray={dashed ? "4 3" : undefined}
      markerEnd="url(#aws-arrow)"
    />
  );
}

function Svg({ vb, label, children }) {
  return (
    <div className="aws-diagram">
      <svg viewBox={vb} role="img" aria-label={label}>
        <defs>
          <marker id="aws-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
            <path d="M0,0 L8,4 L0,8 z" fill="currentColor" />
          </marker>
        </defs>
        {children}
      </svg>
      <p className="aws-diagram-cap">{label}</p>
    </div>
  );
}

const DIAGRAMS = {
  regions: (
    <Svg
      vb="0 0 700 210"
      label="A Region contains multiple Availability Zones; each AZ is one or more independent data centres."
    >
      <rect x="10" y="28" width="450" height="150" rx="6" className="aws-svg-group" />
      <text x="20" y="20" className="aws-svg-t2">Region — ap-south-1 (Mumbai)</text>
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x={28 + i * 142} y={48} width="124" height="112" rx="4" className="aws-svg-box" />
          <text x={90 + i * 142} y={68} textAnchor="middle" className="aws-svg-t">
            {`AZ ${"abc"[i]}`}
          </text>
          <rect x={40 + i * 142} y={80} width="44" height="30" rx="3" className="aws-svg-muted" />
          <rect x={96 + i * 142} y={80} width="44" height="30" rx="3" className="aws-svg-muted" />
          <text x={90 + i * 142} y={132} textAnchor="middle" className="aws-svg-t2">own power</text>
          <text x={90 + i * 142} y={146} textAnchor="middle" className="aws-svg-t2">own network</text>
        </g>
      ))}
      <rect x="500" y="28" width="190" height="150" rx="6" className="aws-svg-group" />
      <text x="510" y="20" className="aws-svg-t2">Region — us-east-1</text>
      <text x="595" y="100" textAnchor="middle" className="aws-svg-t2">fully independent</text>
      <text x="595" y="116" textAnchor="middle" className="aws-svg-t2">an outage here does</text>
      <text x="595" y="132" textAnchor="middle" className="aws-svg-t2">not affect Mumbai</text>
      <text x="235" y="196" textAnchor="middle" className="aws-svg-t2">
        AZs are linked by private low-latency fibre — deploy across two or more to survive a data-centre failure
      </text>
    </Svg>
  ),

  elb: (
    <Svg
      vb="0 0 700 220"
      label="Load balancer spreads traffic across an Auto Scaling group of instances in two Availability Zones."
    >
      <Box x={20} y={88} w={96} h={44} title="Users" tone="muted" />
      <Arrow x1={116} y1={110} x2={158} y2={110} />
      <Box x={164} y={82} w={110} h={56} title="Load balancer" sub="health checks" tone="accent" />
      <rect x="310" y="20" width="370" height="86" rx="5" className="aws-svg-group" />
      <text x="320" y="14" className="aws-svg-t2">Availability Zone a — private subnet</text>
      <Box x={330} y={40} w={104} h={46} title="EC2" sub="running" />
      <Box x={452} y={40} w={104} h={46} title="EC2" sub="running" />
      <Box x={574} y={40} w={92} h={46} title="EC2" sub="scaling out" tone="muted" />
      <rect x="310" y="122" width="370" height="76" rx="5" className="aws-svg-group" />
      <text x="320" y="116" className="aws-svg-t2">Availability Zone b — private subnet</text>
      <Box x={330} y={138} w={104} h={46} title="EC2" sub="running" />
      <Box x={452} y={138} w={104} h={46} title="EC2" sub="unhealthy" tone="muted" />
      <Arrow x1={274} y1={96} x2={304} y2={63} />
      <Arrow x1={274} y1={124} x2={304} y2={158} />
    </Svg>
  ),

  vpc: (
    <Svg
      vb="0 0 700 250"
      label="Public subnets route to an Internet Gateway; private subnets reach out through a NAT Gateway only."
    >
      <rect x="120" y="14" width="566" height="224" rx="6" className="aws-svg-group" />
      <text x="130" y="30" className="aws-svg-t2">VPC — 10.0.0.0/16</text>
      <Box x={14} y={92} w={86} h={44} title="Internet" tone="muted" />
      <Box x={138} y={92} w={92} h={44} title="Internet" sub="Gateway" tone="accent" />
      <Arrow x1={100} y1={114} x2={132} y2={114} />

      <rect x="256" y="44" width="416" height="80" rx="5" className="aws-svg-box" />
      <text x="268" y="62" className="aws-svg-t2">Public subnet — 10.0.1.0/24 · route 0.0.0.0/0 → IGW</text>
      <Box x={272} y={70} w={110} h={42} title="Load balancer" />
      <Box x={398} y={70} w={110} h={42} title="NAT Gateway" />
      <Arrow x1={230} y1={108} x2={250} y2={90} />

      <rect x="256" y="140" width="416" height="86" rx="5" className="aws-svg-box" />
      <text x="268" y="158" className="aws-svg-t2">Private subnet — 10.0.2.0/24 · route 0.0.0.0/0 → NAT</text>
      <Box x={272} y={168} w={110} h={44} title="App servers" />
      <Box x={398} y={168} w={110} h={44} title="Database" />
      <Arrow x1={327} y1={140} x2={327} y2={118} dashed />
      <Arrow x1={453} y1={112} x2={453} y2={166} />
      <text x={578} y={196} textAnchor="middle" className="aws-svg-t2">no inbound path</text>
      <text x={578} y={210} textAnchor="middle" className="aws-svg-t2">from the internet</text>
    </Svg>
  ),

  multiaz: (
    <Svg
      vb="0 0 700 218"
      label="Multi-AZ keeps a synchronous standby for failover; a read replica is asynchronous and serves read traffic."
    >
      <Box x={22} y={86} w={96} h={46} title="Application" tone="muted" />
      <Arrow x1={118} y1={109} x2={158} y2={109} />
      <rect x="164" y="16" width="250" height="186" rx="5" className="aws-svg-group" />
      <text x="174" y="34" className="aws-svg-t2">Multi-AZ — availability</text>
      <Box x={180} y={48} w={104} h={52} title="Primary" sub="AZ a" tone="accent" />
      <Box x={180} y={128} w={104} h={52} title="Standby" sub="AZ b · no reads" />
      <Arrow x1={232} y1={100} x2={232} y2={124} />
      <text x={352} y={82} textAnchor="middle" className="aws-svg-t2">synchronous</text>
      <text x={352} y={96} textAnchor="middle" className="aws-svg-t2">replication</text>
      <text x={352} y={124} textAnchor="middle" className="aws-svg-t2">automatic</text>
      <text x={352} y={138} textAnchor="middle" className="aws-svg-t2">failover</text>

      <rect x="436" y="16" width="250" height="186" rx="5" className="aws-svg-group" />
      <text x="446" y="34" className="aws-svg-t2">Read replica — scale</text>
      <Box x={456} y={48} w={104} h={52} title="Replica 1" sub="serves reads" />
      <Box x={456} y={128} w={104} h={52} title="Replica 2" sub="serves reads" />
      <Arrow x1={284} y1={74} x2={450} y2={74} dashed />
      <text x={584} y={92} textAnchor="middle" className="aws-svg-t2">asynchronous</text>
      <text x={584} y={106} textAnchor="middle" className="aws-svg-t2">→ replica lag</text>
      <text x={584} y={140} textAnchor="middle" className="aws-svg-t2">not an automatic</text>
      <text x={584} y={154} textAnchor="middle" className="aws-svg-t2">failover target</text>
    </Svg>
  ),

  messaging: (
    <Svg
      vb="0 0 700 220"
      label="SQS holds a message for one consumer that pulls it; SNS pushes one message to every subscriber."
    >
      <rect x="10" y="16" width="330" height="188" rx="5" className="aws-svg-group" />
      <text x="20" y="34" className="aws-svg-t2">SQS — queue, one-to-one, pull</text>
      <Box x={24} y={92} w={82} h={44} title="Producer" tone="muted" />
      <Box x={124} y={86} w={100} h={56} title="Queue" sub="stores until read" tone="accent" />
      <Box x={242} y={92} w={86} h={44} title="Consumer" />
      <Arrow x1={106} y1={114} x2={118} y2={114} />
      <Arrow x1={224} y1={114} x2={236} y2={114} />
      <text x={175} y={176} textAnchor="middle" className="aws-svg-t2">exactly one consumer</text>
      <text x={175} y={190} textAnchor="middle" className="aws-svg-t2">processes each message</text>

      <rect x="360" y="16" width="330" height="188" rx="5" className="aws-svg-group" />
      <text x="370" y="34" className="aws-svg-t2">SNS — topic, one-to-many, push</text>
      <Box x={372} y={92} w={82} h={44} title="Publisher" tone="muted" />
      <Box x={470} y={86} w={82} h={56} title="Topic" sub="fan-out" tone="accent" />
      <Arrow x1={454} y1={114} x2={464} y2={114} />
      <Box x={578} y={46} w={98} h={38} title="Lambda" />
      <Box x={578} y={96} w={98} h={38} title="SQS queue" />
      <Box x={578} y={146} w={98} h={38} title="Email / SMS" />
      <Arrow x1={552} y1={104} x2={572} y2={68} />
      <Arrow x1={552} y1={114} x2={572} y2={114} />
      <Arrow x1={552} y1={126} x2={572} y2={160} />
    </Svg>
  ),

  hybrid: (
    <Svg
      vb="0 0 700 200"
      label="Hybrid architecture: on-premises workloads joined to a VPC over Direct Connect or VPN, with DMS replicating data."
    >
      <rect x="10" y="26" width="250" height="150" rx="5" className="aws-svg-group" />
      <text x="20" y="44" className="aws-svg-t2">On-premises data centre</text>
      <Box x={26} y={56} w={104} h={44} title="App servers" />
      <Box x={144} y={56} w={104} h={44} title="Oracle DB" />
      <Box x={26} y={116} w={222} h={40} title="Stays on-site" sub="latency or data-residency reasons" tone="muted" />

      <Box x={294} y={62} w={112} h={52} title="Direct Connect" sub="or VPN" tone="accent" />
      <Arrow x1={260} y1={88} x2={288} y2={88} />
      <Arrow x1={406} y1={88} x2={434} y2={88} />

      <rect x="440" y="26" width="250" height="150" rx="5" className="aws-svg-group" />
      <text x="450" y="44" className="aws-svg-t2">AWS Region — VPC</text>
      <Box x={456} y={56} w={104} h={44} title="EC2 / EKS" />
      <Box x={574} y={56} w={104} h={44} title="Aurora" />
      <Box x={456} y={116} w={222} h={40} title="DMS keeps both in sync" sub="during a phased migration" tone="muted" />
    </Svg>
  ),
};

/* =========================================================================
   Content — 8 modules, 80 questions
   ========================================================================= */

const MODULES = [
  {
    n: 1,
    co: 1,
    title: "AWS Cloud Foundations and IAM",
    blurb:
      "What the cloud is, how AWS lays out the world geographically, and who is allowed to do what inside your account.",
    questions: [
      {
        q: "What is cloud computing?",
        a: "The on-demand delivery of IT resources — compute, storage, databases, networking — over the internet, paid for as you use them. Instead of buying, racking and maintaining servers, you rent capacity from a provider and release it when you are done. The essential shift is from capital expenditure on hardware you own to operating expenditure on capacity you rent, and from procurement cycles measured in months to provisioning measured in minutes.",
      },
      {
        q: "What is AWS? Write the full form of AWS.",
        term: "AWS = Amazon Web Services",
        a: "Amazon Web Services is Amazon's cloud computing platform, offering more than 200 services spanning compute, storage, networking, databases, analytics, machine learning and security. You provision these through a web console, a command-line interface or an API, and pay only for what you consume. It is the largest public cloud provider by market share.",
      },
      {
        q: "What is an AWS Region?",
        a: "A Region is a separate geographic area in which AWS operates a cluster of data centres — for example ap-south-1 (Mumbai) or us-east-1 (N. Virginia). Each Region is fully independent of the others, so a failure in one does not spread. You choose a Region based on latency to your users, data-residency law, which services are available there, and price, since prices differ by Region.",
        diagram: "regions",
      },
      {
        q: "What is an Availability Zone in AWS?",
        a: "An Availability Zone (AZ) is one or more discrete data centres inside a Region, each with independent power, cooling and networking. AZs in the same Region are far enough apart to be isolated from most local failures such as a flood or a power outage, but are connected by private, low-latency fibre. Deploying across two or more AZs is the standard way to make an application survive the loss of a single data centre.",
      },
      {
        q: "What is the AWS Shared Responsibility Model?",
        a: "A division of security duties between AWS and the customer. AWS is responsible for security *of* the cloud — the physical facilities, hardware, host operating system and virtualisation layer. The customer is responsible for security *in* the cloud — their data, guest OS patching, IAM configuration, encryption choices and network rules. The exact dividing line moves by service: it is larger for EC2 than for a managed service such as S3 or Lambda.",
      },
      {
        q: "What is AWS IAM? Write the full form of IAM.",
        term: "IAM = Identity and Access Management",
        a: "IAM is the service that controls who can sign in to your AWS account and what they are permitted to do once in. It manages identities — users, groups and roles — and the policies attached to them. IAM is a global service rather than a per-Region one, and it costs nothing to use.",
      },
      {
        q: "What is an IAM user?",
        a: "An identity representing a single person or application, with long-term credentials in your account: a password for console sign-in and/or access keys for programmatic access. Each user should map to exactly one human or workload, because a shared user destroys your audit trail. Current best practice is to prefer federated identities through IAM Identity Center, or roles, over long-lived IAM users with permanent keys.",
      },
      {
        q: "What is an IAM group, and why is it used?",
        a: "A group is a collection of IAM users that share a set of permissions. You attach policies to the group once and every member inherits them, so onboarding becomes \"add this person to the Developers group\" rather than copying a dozen policies by hand. Two limits worth knowing: groups cannot be nested, and a group is not an identity — you cannot sign in as a group or attach it to a resource.",
      },
      {
        q: "What is an IAM policy?",
        a: "A JSON document that lists permissions: which **Actions** are allowed or denied, on which **Resources**, under which **Conditions**. Identity-based policies attach to a user, group or role; resource-based policies attach to the resource itself, such as an S3 bucket policy. Evaluation is deny-by-default — anything not explicitly allowed is refused — and an explicit Deny always overrides an Allow.",
      },
      {
        q: "What is an IAM role? Mention one basic IAM security best practice.",
        a: "A role is an identity carrying permissions that can be assumed temporarily by a trusted entity — an EC2 instance, a Lambda function, a user from another AWS account, or a federated corporate identity. Assuming a role issues short-lived credentials rather than storing permanent keys on disk, which is why roles are preferred for anything automated. Best practice: apply **least privilege** — grant only the permissions actually needed and review them regularly — and protect the root user with MFA, using it only for the few tasks that require it.",
      },
    ],
  },

  {
    n: 2,
    co: 4,
    title: "AWS Compute and Storage Services",
    blurb: "Virtual servers you control, the ways you pay for them, and the three different shapes AWS storage comes in.",
    questions: [
      {
        q: "What is Amazon EC2? Write the full form of EC2.",
        term: "EC2 = Elastic Compute Cloud",
        a: "EC2 provides resizable virtual servers in the cloud. You pick an operating system image, a size and a network, and the instance boots in a minute or two. You keep full control of the operating system — which also means patching, hardening and monitoring it are your responsibility, not Amazon's.",
      },
      {
        q: "What is an EC2 instance?",
        a: "A single running virtual machine, launched from an AMI (Amazon Machine Image) on the EC2 service. It has CPU, memory, storage and network attached, lives inside a subnet of a VPC, and is guarded by one or more security groups. You control its whole lifecycle: launch, stop, start and terminate.",
      },
      {
        q: "What are EC2 instance types? Give any two examples of workload categories.",
        a: "Instance types are pre-defined hardware profiles, named as family-generation-size, such as t3.micro or m6i.large. Families are tuned for different workloads. **Compute-optimised (C family)** suits CPU-heavy work such as batch processing, media encoding and scientific modelling. **Memory-optimised (R and X families)** suits in-memory databases and large caches. Others include general purpose (T, M), storage optimised (I, D) for high local disk throughput, and accelerated computing (P, G) for GPU workloads.",
      },
      {
        q: "Name any three EC2 pricing models.",
        a: "**On-Demand** — pay per second with no commitment; best for spiky or unpredictable workloads. **Reserved Instances / Savings Plans** — commit to one or three years in exchange for a large discount; best for steady baseline load. **Spot Instances** — use spare capacity at up to around 90% off, accepting that AWS may reclaim the instance with two minutes' notice; suitable only for interruptible work. A fourth, **Dedicated Hosts**, exists for licensing and compliance requirements.",
      },
      {
        q: "Write any four common stages in the lifecycle of an EC2 instance.",
        a: "**pending** — the instance is booting and not yet billable for compute. **running** — in service and billing. **stopping / stopped** — shut down; the EBS root volume and its data are kept and you stop paying for compute, but still pay for storage. **shutting-down / terminated** — permanently deleted; the instance cannot be recovered. A running instance can also be **rebooting**, which keeps the same instance and IP.",
      },
      {
        q: "What is EC2 Auto Scaling, and why is it used?",
        a: "Auto Scaling adds or removes EC2 instances in a group automatically, driven by metrics such as average CPU or request count, by a schedule, or by a predictive model. It also replaces instances that fail health checks. The result is elasticity — you pay only for the capacity demand actually requires — and resilience, because a dead instance is replaced without anyone being paged.",
      },
      {
        q: "What is Elastic Load Balancing (ELB)?",
        a: "ELB distributes incoming traffic across multiple targets — EC2 instances, containers or IP addresses — in one or more Availability Zones. It health-checks each target and stops sending traffic to unhealthy ones, which is what makes multi-AZ deployment useful. The three main types are the Application Load Balancer (HTTP/HTTPS, layer 7), the Network Load Balancer (TCP/UDP, layer 4, very high throughput) and the Gateway Load Balancer (for third-party virtual appliances).",
        diagram: "elb",
      },
      {
        q: "What is Amazon S3, and what type of data can be stored in it?",
        term: "S3 = Simple Storage Service",
        a: "S3 is object storage: you put files (\"objects\") into \"buckets\", each identified by a key, with essentially unlimited capacity and eleven nines of durability. It suits any unstructured or semi-structured data — images, video, backups, logs, data-lake files, static website assets, software artefacts. It is not a filesystem and not a block device, so you cannot install an operating system on it or run a database directly from it.",
      },
      {
        q: "What are S3 storage classes? Name any three.",
        a: "Storage classes trade retrieval speed and availability against price, so you can match cost to how often data is actually read. **S3 Standard** — frequently accessed data, millisecond retrieval. **S3 Standard-Infrequent Access** — data read rarely but needed instantly when it is, cheaper storage with a retrieval charge. **S3 Glacier Deep Archive** — long-term archives where a wait of up to twelve hours is acceptable, at the lowest price AWS offers. Others include Intelligent-Tiering, One Zone-IA, Glacier Instant Retrieval and Glacier Flexible Retrieval.",
      },
      {
        q: "Differentiate between Amazon EBS, Amazon EFS and Amazon S3.",
        a: "All three store data, but they present it in fundamentally different shapes, and that shape determines what you can do with it.",
        table: {
          head: ["", "Amazon EBS", "Amazon EFS", "Amazon S3"],
          rows: [
            ["Type", "Block storage", "File storage", "Object storage"],
            ["Analogy", "A hard disk", "A shared network drive", "A warehouse reached by API"],
            ["Attached to", "One instance at a time", "Many instances at once", "Nothing — accessed over HTTPS"],
            ["Scope", "One Availability Zone", "Regional, multi-AZ", "Regional, multi-AZ"],
            ["Scaling", "You resize the volume", "Grows automatically", "Effectively unlimited"],
            ["Typical use", "Boot volume, database files", "Shared application content", "Backups, logs, media, data lake"],
          ],
        },
      },
    ],
  },

  {
    n: 3,
    co: 4,
    title: "AWS Networking and Content Delivery",
    blurb: "Your own private network in the cloud, the rules controlling traffic through it, and getting content close to users.",
    questions: [
      {
        q: "What is computer networking in the context of cloud computing?",
        a: "Networking is how resources find and communicate with each other using addresses, routes and rules. In the cloud it is software-defined: you create networks, subnets, routes and firewalls through an API in seconds, rather than physically cabling switches and routers. The underlying concepts — IP addressing, CIDR, routing, firewalls — are exactly the same as on-premises; only the provisioning method changes.",
      },
      {
        q: "What is Amazon VPC? Write the full form of VPC.",
        term: "VPC = Virtual Private Cloud",
        a: "A VPC is your own logically isolated network inside an AWS Region, using an IP range you choose, such as 10.0.0.0/16. You control its subnets, route tables, gateways and firewall rules. Nothing outside the VPC can reach in unless you deliberately create a path, which makes the VPC the primary network security boundary in AWS.",
      },
      {
        q: "What is a subnet in a VPC?",
        a: "A subnet is a slice of the VPC's IP range that lives in exactly one Availability Zone. You place resources such as EC2 instances into subnets, so spreading subnets across AZs is how you achieve high availability. A subnet cannot span two AZs, and its CIDR block must fit inside the VPC's; AWS also reserves five addresses in every subnet.",
      },
      {
        q: "What is the basic difference between a public subnet and a private subnet?",
        a: "The difference is purely routing — there is no \"public\" checkbox. A **public** subnet has a route table entry sending 0.0.0.0/0 to an Internet Gateway, so resources with a public IP can reach and be reached from the internet. A **private** subnet has no such route; it typically reaches the internet outbound only, via a NAT Gateway that sits in a public subnet. Load balancers and bastion hosts go in public subnets; application servers and databases go in private ones.",
        diagram: "vpc",
      },
      {
        q: "What is a route table in Amazon VPC?",
        a: "A set of rules deciding where traffic leaving a subnet is sent, matched most-specific-destination first. Every subnet is associated with exactly one route table, though one table can serve many subnets. A local route covering the VPC's own CIDR is always present and cannot be removed, which is why resources in different subnets of the same VPC can always reach each other.",
      },
      {
        q: "What is a Security Group?",
        a: "A virtual firewall attached to a resource's network interface, most often an EC2 instance. It is **stateful**: if you allow a request inbound, the response is automatically allowed back out without a matching rule. Rules are allow-only — you cannot write a deny rule. The contrast is a Network ACL, which operates at the subnet level, is stateless, and does support explicit deny.",
      },
      {
        q: "What is the purpose of inbound and outbound rules in a Security Group?",
        a: "Inbound rules define which traffic may reach the resource — for example TCP 443 from 0.0.0.0/0 for a public web server. Outbound rules define which traffic the resource may originate — for example TCP 5432 to the database. By default a new security group denies all inbound and allows all outbound. Referencing another security group as the source, rather than a hard-coded IP range, is both safer and survives instances being replaced.",
      },
      {
        q: "What is VPC Peering?",
        a: "A direct, private network connection between two VPCs, letting resources in each address the other by private IP without traversing the internet. It works across accounts and across Regions. Two constraints matter: peering is **not transitive** — if A peers with B and B peers with C, A still cannot reach C — and the CIDR ranges must not overlap. Once you have many VPCs, a Transit Gateway scales far better than a mesh of peerings.",
      },
      {
        q: "What is AWS Direct Connect?",
        a: "A dedicated private network link from your data centre or office to AWS, bypassing the public internet entirely. It delivers more consistent latency, higher and more predictable bandwidth, and can lower data-transfer costs at volume. The trade-off is lead time: provisioning takes weeks, so a site-to-site VPN over the internet is the usual interim solution or backup path.",
      },
      {
        q: "What is Amazon CloudFront, and why is it used?",
        a: "CloudFront is AWS's content delivery network (CDN). It caches content at hundreds of edge locations worldwide so that a user in Chennai is served from a nearby edge rather than from an origin bucket in Virginia. The benefits are lower latency for users, much less load and cost on the origin, and security at the edge — TLS termination, AWS Shield DDoS protection and optional AWS WAF rules.",
      },
    ],
  },

  {
    n: 4,
    co: 4,
    title: "AWS Database Services",
    blurb: "Managed relational databases, how they stay available, and when a non-relational store is the better answer.",
    questions: [
      {
        q: "What is a database?",
        a: "An organised collection of data together with software to store, retrieve and manage it. **Relational** databases store data in tables of rows and columns with a defined schema and are queried with SQL; **non-relational** (NoSQL) databases use more flexible structures such as key-value pairs, documents or graphs. The right choice is driven by the access pattern and the shape of the data, not by which is newer.",
      },
      {
        q: "What is Amazon RDS? Write the full form of RDS.",
        term: "RDS = Relational Database Service",
        a: "RDS is a managed relational database service. AWS handles provisioning, patching, automated backups, failover and minor version upgrades; you keep control of the schema, the queries and the data. What you give up is operating-system access — you cannot SSH into an RDS host — which is precisely the trade for not having to run the server yourself.",
      },
      {
        q: "Name any three database engines supported by Amazon RDS.",
        a: "MySQL, PostgreSQL and Microsoft SQL Server. RDS also supports MariaDB, Oracle, IBM Db2 and Amazon Aurora, which is AWS's own MySQL- and PostgreSQL-compatible engine.",
      },
      {
        q: "What is an RDS Multi-AZ deployment?",
        a: "RDS maintains a standby copy of your database in a different Availability Zone, kept identical by synchronous replication. If the primary instance or its AZ fails, RDS promotes the standby automatically — usually within one to two minutes — and the DNS endpoint your application uses is repointed, so no connection-string change is needed. The standby serves no read traffic; it exists purely for availability.",
        diagram: "multiaz",
      },
      {
        q: "Why is Multi-AZ useful for database availability?",
        a: "It removes the single data centre as a single point of failure, and it makes routine maintenance non-disruptive because patching and backups run against the standby rather than the live database. Failover is automatic, so recovery does not depend on someone being awake and available. The cost is roughly double, since you pay for a standby that serves no traffic — that is the price of the guarantee.",
      },
      {
        q: "What is an RDS Read Replica?",
        a: "An asynchronous copy of the database that you can query. Read replicas exist to scale **read** traffic — reporting, dashboards, analytics — by moving that load off the primary. Because replication is asynchronous there is some replica lag, so reads may be very slightly stale. Unlike a Multi-AZ standby, a replica is not an automatic failover target, though you can promote one manually into a standalone database.",
      },
      {
        q: "What is Amazon Aurora?",
        a: "Aurora is AWS's own MySQL- and PostgreSQL-compatible database engine, rebuilt for the cloud. It separates compute from a distributed storage layer that keeps six copies of your data across three Availability Zones, self-heals, and grows automatically. AWS claims several times the throughput of standard MySQL or PostgreSQL on equivalent hardware. Aurora Serverless adds automatic capacity scaling for intermittent or unpredictable workloads.",
      },
      {
        q: "What is Amazon DynamoDB?",
        a: "A fully managed NoSQL key-value and document database delivering single-digit-millisecond response times at any scale. There are no servers to manage and no fixed capacity — it scales horizontally by partitioning on your key, and on-demand mode adjusts throughput automatically. You design the table around your queries up front, because there is no SQL, no joins and no ad-hoc querying without planning for it.",
      },
      {
        q: "State one basic difference between Amazon RDS and Amazon DynamoDB.",
        a: "RDS is relational with a fixed schema, SQL and joins, and scales primarily by making the instance bigger (vertical scaling). DynamoDB is non-relational, schema-flexible, has no joins, and scales horizontally with no practical ceiling.",
        table: {
          head: ["", "Amazon RDS", "Amazon DynamoDB"],
          rows: [
            ["Model", "Relational (tables, rows)", "Key-value / document"],
            ["Schema", "Fixed, defined up front", "Flexible per item"],
            ["Query language", "SQL, including joins", "Key-based API, no joins"],
            ["Scaling", "Mainly vertical", "Horizontal, automatic"],
            ["Latency at scale", "Varies with load and query", "Consistent single-digit ms"],
            ["Best when", "Relationships and ad-hoc queries matter", "Access pattern is known and fixed"],
          ],
        },
      },
      {
        q: "What are DynamoDB Streams used for?",
        a: "A DynamoDB Stream is a time-ordered log of every item-level change — insert, update, delete — in a table, retained for 24 hours. Applications, most commonly Lambda functions, read the stream to react to those changes: updating a search index, sending a notification, replicating to another Region, maintaining an aggregate, or feeding an event-driven pipeline. It turns a database table into an event source without any polling.",
      },
    ],
  },

  {
    n: 5,
    co: 3,
    title: "AWS Management Tools and Cost Management",
    blurb: "Seeing what your resources are doing, who did what to them, and what all of it is costing you.",
    questions: [
      {
        q: "What is Amazon CloudWatch?",
        a: "CloudWatch is AWS's monitoring and observability service. It collects metrics, logs and events from AWS services and from your own applications, presents them on dashboards, and triggers alarms and automated actions when a threshold is crossed. Its question is \"**how are my resources performing?**\" — an alarm can notify a team, scale an Auto Scaling group, or reboot an instance.",
      },
      {
        q: "Give any two examples of resources or metrics monitored using CloudWatch.",
        a: "EC2 **CPUUtilization**, and RDS **FreeStorageSpace** or **DatabaseConnections**. Other common ones: ELB request count and target response time, Lambda invocations, errors and duration, S3 bucket size, and DynamoDB consumed capacity. You can also publish **custom metrics** of your own — application memory use, queue depth, or a business figure such as orders per minute — which is how CloudWatch covers things AWS cannot see for you.",
      },
      {
        q: "What is AWS CloudTrail?",
        a: "CloudTrail records API activity in your account: who made each call, from which IP address, at what time, against which resource, and whether it succeeded. It is the audit log, used for security investigation, compliance evidence and answering \"who deleted that bucket?\". Events can be delivered to S3 and CloudWatch Logs for long-term retention and alerting.",
      },
      {
        q: "State one basic difference between CloudWatch and CloudTrail.",
        a: "CloudWatch watches **performance** — metrics and logs describing how resources are behaving. CloudTrail watches **activity** — the API calls made against your account. The quickest test: \"why is this instance slow?\" is a CloudWatch question; \"who stopped this instance?\" is a CloudTrail question. In a real investigation you usually need both.",
      },
      {
        q: "What is AWS Config?",
        a: "Config records the configuration of your resources over time and evaluates them against rules you define — for example \"every S3 bucket must have encryption enabled\" or \"no security group may allow 0.0.0.0/0 on port 22\". It flags non-compliant resources, keeps a configuration history you can replay, and can trigger automatic remediation. Its question is \"**is this resource configured the way it is supposed to be?**\"",
      },
      {
        q: "What is AWS Systems Manager?",
        a: "A collection of operational tools for managing instances and applications at scale: **Run Command** to execute commands across a fleet, **Patch Manager** for OS patching, **Parameter Store** for configuration and secrets, and **Session Manager** for shell access with no SSH port open and no bastion host. It works on EC2, on-premises servers and other clouds through the SSM Agent, which makes it useful in hybrid estates.",
      },
      {
        q: "What is the AWS Free Tier?",
        a: "A set of allowances letting you use AWS at no charge while learning or evaluating. It comes in three forms: **always free** (for example one million Lambda requests per month), **twelve months free** from account creation (for example 750 hours of a t2/t3.micro EC2 instance per month), and **short-term trials**. Going past an allowance bills at normal rates, which is exactly why a budget alert should be the first thing you configure.",
      },
      {
        q: "What is AWS Budgets, and why is it useful?",
        a: "Budgets lets you set a planned spend or usage figure and be alerted by email or SNS when actual or forecast cost crosses a threshold you choose. It turns cost from something you discover in next month's invoice into something you hear about on day four, while you can still act. Budget Actions go further and can apply a restrictive IAM policy or stop resources automatically when a limit is breached.",
      },
      {
        q: "What information can you obtain from AWS Billing and Cost Management?",
        a: "Current and forecast charges, a breakdown by service, Region, linked account and tag, historical trends and filtering through **Cost Explorer**, line-item detail via the **Cost and Usage Report**, and coverage and utilisation figures for Savings Plans and Reserved Instances. Cost allocation tags are what make the breakdown meaningful — without a tagging standard, most of your spend lands in an undifferentiated bucket nobody can act on.",
      },
      {
        q: "What is AWS Trusted Advisor?",
        a: "An automated review of your account against AWS best practice across five categories: cost optimisation, performance, security, fault tolerance and service limits. It flags idle load balancers, over-provisioned instances, publicly readable S3 buckets, missing MFA on the root user, and quotas you are approaching. A core set of checks is available to every account; the full set requires Business or Enterprise Support.",
      },
    ],
  },

  {
    n: 6,
    co: 2,
    title: "AWS Security and Compliance",
    blurb: "Where AWS's responsibility ends and yours begins, and the services that help you hold up your half.",
    questions: [
      {
        q: "Why is security important in cloud computing?",
        a: "Because your data sits on infrastructure you do not physically control, reachable over a network, where a single misconfiguration can expose it globally within seconds. The cloud makes provisioning fast, which makes mistakes fast too — a public bucket or an open security group is one click away. Regulatory obligations around data residency, privacy and sector rules also do not transfer to the provider; they remain yours regardless of where the data is stored.",
      },
      {
        q: "What is the AWS Shared Responsibility Model?",
        a: "The same model introduced in Module 1: AWS secures the cloud, you secure what you put in it. The point worth adding here is that the dividing line shifts with the service model. On EC2 you patch the operating system; on RDS AWS patches it for you; on Lambda there is no operating system for you to see at all. Knowing where the line falls for each service you use is the practical skill, not reciting the diagram.",
      },
      {
        q: "Under the Shared Responsibility Model, what is AWS generally responsible for?",
        a: "The physical data centres and their security, the hardware, the host operating system and virtualisation layer, the global network infrastructure, and the software of the managed services themselves. Informally: everything from the concrete floor up to the hypervisor. AWS also holds the compliance certifications for that layer, which you inherit as evidence for your own audits.",
      },
      {
        q: "Under the Shared Responsibility Model, what is the customer generally responsible for?",
        a: "Your data and how it is classified, your encryption choices and key management, the guest operating system and its patching, your application code and its dependencies, IAM users, roles and permissions, network configuration such as security groups and route tables, and any industry-specific controls you are obliged to implement.",
      },
      {
        q: "What is AWS KMS? Write the full form of KMS.",
        term: "KMS = Key Management Service",
        a: "KMS creates, stores and manages the encryption keys used across AWS services. Keys never leave the service in plaintext, their use is controlled by IAM policies and key policies, and every use is recorded in CloudTrail. Most AWS services integrate with it directly, so enabling encryption on an S3 bucket, an EBS volume or an RDS database is a checkbox backed by KMS rather than a project.",
      },
      {
        q: "Why is encryption used to protect data?",
        a: "Encryption renders data unreadable without the key, so a stolen disk, an intercepted network packet or a mistakenly shared snapshot does not automatically become a data breach. Two forms matter: **at rest**, covering stored data through KMS-backed service encryption, and **in transit**, covering data on the wire through TLS. Many compliance regimes require both, and on AWS both are usually a configuration setting rather than an engineering effort.",
      },
      {
        q: "What is AWS WAF? Write the full form of WAF.",
        term: "WAF = Web Application Firewall",
        a: "WAF inspects HTTP and HTTPS requests before they reach your application and blocks those matching rules you configure. It defends against application-layer attacks such as SQL injection and cross-site scripting, and can also rate-limit, geo-block or allow-list. It attaches to CloudFront, an Application Load Balancer, API Gateway or AppSync, and ships with AWS-managed rule groups so you do not have to write every rule yourself.",
      },
      {
        q: "What is AWS Shield?",
        a: "A managed DDoS protection service. **Shield Standard** is enabled automatically for every AWS customer at no extra cost and absorbs the common network- and transport-layer floods. **Shield Advanced** is a paid tier adding detection and mitigation for larger and application-layer attacks, 24/7 access to the AWS Shield Response Team, detailed attack diagnostics, and cost protection so an attack-driven scaling event does not land you with the bill.",
      },
      {
        q: "What type of attacks is AWS Shield designed to help protect against?",
        a: "Distributed Denial of Service attacks — attempts to overwhelm a target with traffic from many sources so that legitimate users cannot get through. Shield Standard covers layer 3 and 4 volumetric attacks such as SYN floods, UDP reflection and amplification. Shield Advanced extends to layer 7 attacks such as HTTP floods, which are typically mitigated alongside AWS WAF rate-based rules.",
      },
      {
        q: "Write any three basic AWS security best practices.",
        a: "**Least privilege** — grant only the permissions actually required, prefer roles over long-lived access keys, and review entitlements regularly. **Protect the root user** — enable MFA, create no access keys for it, and use it only for the handful of tasks that genuinely require it. **Encrypt everything and log everything** — KMS at rest, TLS in transit, and CloudTrail enabled in every Region with logs delivered somewhere they cannot be quietly deleted.",
      },
    ],
  },

  {
    n: 7,
    co: 1,
    title: "Application Integration and Architectural Best Practices",
    blurb: "Letting components talk without depending on each other, and the vocabulary architects use to judge a design.",
    questions: [
      {
        q: "What is application integration?",
        a: "Connecting separate applications or components so they can exchange data and trigger each other's work. The cloud pattern is **loose coupling**: components communicate through a queue or a topic rather than calling each other directly, so one slow or failed component does not drag the rest down with it. Services like SQS, SNS, EventBridge and Step Functions exist to make that decoupling easy.",
      },
      {
        q: "What is Amazon SQS? Write the full form of SQS.",
        term: "SQS = Simple Queue Service",
        a: "SQS is a fully managed message queue. A producer puts a message in, SQS stores it durably, and a consumer pulls it out, processes it and deletes it. This decouples the two sides: the producer never waits for the consumer, and a traffic spike is absorbed by the queue instead of dropping work. **Standard** queues offer at-least-once delivery with best-effort ordering; **FIFO** queues offer exactly-once processing in strict order at lower throughput.",
      },
      {
        q: "What is a message queue?",
        a: "A buffer holding messages between a sender and a receiver so that neither needs to be available at the same moment. It smooths traffic spikes, lets the consumer be scaled independently of the producer, and retries work automatically when processing fails. Messages that repeatedly fail are moved to a **dead-letter queue** so they can be inspected without blocking everything behind them.",
      },
      {
        q: "What is Amazon SNS? Write the full form of SNS.",
        term: "SNS = Simple Notification Service",
        a: "SNS is a publish/subscribe messaging service. A publisher sends one message to a **topic**, and SNS fans it out to every subscriber — Lambda functions, SQS queues, HTTP endpoints, email addresses or SMS numbers. It is push-based, so subscribers are delivered to rather than having to poll, and the publisher has no knowledge of who is listening.",
      },
      {
        q: "State one basic difference between Amazon SQS and Amazon SNS.",
        a: "SQS is one-to-one and pull-based: a message is processed by exactly one consumer, which polls for it, and it waits in the queue until someone does. SNS is one-to-many and push-based: a message is delivered immediately to every subscriber and is not retained for later. The common production pattern uses both — SNS fans out to several SQS queues, giving each downstream service its own durable buffer.",
        diagram: "messaging",
        table: {
          head: ["", "Amazon SQS", "Amazon SNS"],
          rows: [
            ["Pattern", "Queue (point to point)", "Publish / subscribe"],
            ["Consumers", "Exactly one per message", "Every subscriber gets a copy"],
            ["Delivery", "Consumer pulls (polls)", "SNS pushes"],
            ["Retention", "Up to 14 days", "Not retained; delivered or retried"],
            ["Use it for", "Buffering and levelling work", "Fan-out and notifications"],
          ],
        },
      },
      {
        q: "What is AWS Step Functions?",
        a: "A serverless workflow service. You define a state machine — a sequence of steps with branching, parallel execution, retries, error handling and waits — and Step Functions runs it, tracking the state of every execution. It replaces the orchestration code you would otherwise hand-write and debug across several Lambda functions, and gives you a visual execution history that makes a failed run easy to diagnose.",
      },
      {
        q: "What is Amazon SWF? Write the full form of SWF.",
        term: "SWF = Simple Workflow Service",
        note:
          "Legacy service. AWS recommends Step Functions for all new workflows — SWF is still supported but is effectively background knowledge.",
        a: "SWF is the older workflow coordination service, in which you write your own \"deciders\" and \"workers\" in application code while SWF tracks task state and execution history. It offers very fine-grained control, at the cost of significantly more code to write and operate. For anything new, Step Functions is simpler, serverless and cheaper to run.",
      },
      {
        q: "What is the AWS Well-Architected Framework?",
        a: "A set of design principles and review questions AWS publishes for evaluating a workload, organised into six pillars: **Operational Excellence, Security, Reliability, Performance Efficiency, Cost Optimization** and **Sustainability**. The accompanying Well-Architected Tool walks you through the questions and produces a prioritised list of risks. It is a review method rather than a product — nothing is deployed, and nothing is billed.",
      },
      {
        q: "What is meant by scalability and elasticity in cloud computing?",
        a: "**Scalability** is the ability to handle more load by adding resources — horizontally, meaning more instances, or vertically, meaning bigger instances. **Elasticity** is doing that automatically and in both directions, so capacity tracks demand minute by minute and you release what you no longer need. A system can be scalable without being elastic; elasticity is what converts scalability into an actual cost saving.",
      },
      {
        q: "Explain High Availability, Fault Tolerance and Cost Optimization in simple terms.",
        a: "These three are frequently confused, particularly the first two. The distinction is about whether users notice the failure.",
        table: {
          head: ["Concept", "In plain terms", "Typical implementation"],
          rows: [
            [
              "High Availability",
              "Stays usable through a failure, possibly with a brief interruption",
              "Instances across two AZs behind a load balancer; RDS Multi-AZ",
            ],
            [
              "Fault Tolerance",
              "Keeps working through a failure with no interruption at all",
              "Full redundancy with live spare capacity; costs considerably more",
            ],
            [
              "Cost Optimization",
              "Delivering the required outcome at the lowest sustained cost",
              "Right-sizing, Savings Plans, Spot for interruptible work, deleting idle resources",
            ],
          ],
        },
      },
    ],
  },

  {
    n: 8,
    co: 2,
    title: "AWS Migration and Hybrid Architecture",
    blurb: "Getting existing workloads into AWS, and the common reality that some of them are staying where they are.",
    questions: [
      {
        q: "What is cloud migration?",
        a: "Moving applications, data and workloads from on-premises data centres, or from another cloud, into a cloud provider. AWS frames the options as the **seven Rs**: rehost (lift and shift), replatform, repurchase, refactor, retire, retain and relocate. Most large migrations apply several of these across a portfolio rather than committing to one strategy for everything.",
      },
      {
        q: "What is meant by a hybrid cloud architecture?",
        a: "A design where part of the workload runs in the cloud and part remains on-premises, joined by a private network link — Direct Connect or VPN — and operated as one environment. The reasons are usually concrete: data that cannot legally leave the country, latency-sensitive equipment on a factory floor or telecom site, hardware not yet depreciated, or a migration being done in phases. AWS Outposts, Local Zones and Storage Gateway all exist to serve this pattern.",
        diagram: "hybrid",
      },
      {
        q: "What is AWS Database Migration Service (AWS DMS)?",
        term: "DMS = Database Migration Service",
        a: "DMS migrates databases into AWS while the source database stays online and serving traffic. It supports **homogeneous** migrations (Oracle to Oracle) and **heterogeneous** ones (Oracle to PostgreSQL), where the AWS Schema Conversion Tool converts schema and stored procedures first. Beyond one-off moves it can run continuous replication, which is what keeps downtime at cutover down to minutes.",
      },
      {
        q: "What type of resources can be migrated using AWS DMS?",
        a: "Databases and data stores. Sources include commercial and open-source relational engines — Oracle, SQL Server, MySQL, PostgreSQL, MariaDB, Db2, SAP ASE — plus MongoDB and S3. Targets include those same engines on RDS, Amazon Aurora, Redshift, DynamoDB, DocumentDB, S3, Kinesis, OpenSearch and Kafka. DMS moves **data**, and with the Schema Conversion Tool the **schema** — it does not migrate servers, operating systems or application code.",
      },
      {
        q: "Give one simple example where an organization might use AWS DMS.",
        a: "A company runs an on-premises Oracle database under an expensive licence and wants to move to Amazon Aurora PostgreSQL. They first run the Schema Conversion Tool to convert the schema and PL/SQL. DMS then performs a full load followed by change data capture, keeping Aurora in sync while the application is tested against it. At a chosen cutover window the application is repointed and the old database retired — total downtime measured in minutes rather than a lost weekend.",
      },
      {
        q: "What is AWS Server Migration Service (SMS)?",
        term: "SMS = Server Migration Service",
        note:
          "Retired. AWS discontinued SMS on 31 March 2022 and replaced it with AWS Application Migration Service (MGN), which was itself renamed AWS Transform MGN in June 2026. Answer as asked for the assignment, but never choose SMS for real work.",
        a: "SMS was an agentless service that automated and scheduled incremental replication of on-premises virtual machines into AWS, producing AMIs you could then launch as EC2 instances. It could handle multi-server application stacks together rather than one server at a time, and could resume a failed replication run from the point of failure instead of restarting it.",
      },
      {
        q: "What is AWS Snowball?",
        a: "Snowball is a physical, ruggedised storage device that AWS ships to your site. You copy data onto it across your local network, ship it back, and AWS loads the contents into S3. Data is encrypted using KMS keys and the device is tamper-resistant and tracked. It exists for one purpose: moving volumes of data that would take an impractical amount of time over a network connection.",
      },
      {
        q: "When would an organization use AWS Snowball instead of transferring over the internet?",
        a: "When transfer time over the available bandwidth is unacceptable. The rough test is terabytes of data against a link measured in tens of megabits per second. It also applies when bandwidth is metered or expensive, when the site is remote or poorly connected, or when saturating the link for days would disrupt production traffic. A practical rule of thumb: if the transfer would take more than about a week, price up a Snowball.",
        table: {
          head: ["Data volume", "Over 100 Mbps", "Over 1 Gbps", "Reasonable choice"],
          rows: [
            ["1 TB", "~1 day", "~2.5 hours", "Network"],
            ["10 TB", "~10 days", "~1 day", "Borderline"],
            ["100 TB", "~3 months", "~10 days", "Snowball"],
            ["1 PB", "~2.5 years", "~3 months", "Snowball, several devices"],
          ],
        },
      },
      {
        q: "What is AWS Snowball Edge?",
        a: "Snowball Edge is the current generation of the device, adding on-board compute to the storage — you can run EC2 instances and AWS Lambda functions directly on it. It comes in **Storage Optimized** and **Compute Optimized** configurations. That makes it useful not only for bulk transfer but for processing data at the edge in places with no reliable connectivity: a ship, a mine, a remote cell site or a military deployment.",
      },
      {
        q: "State one basic difference between AWS Snowball and AWS Snowball Edge.",
        a: "The original Snowball was **storage only** — purely a data-transfer appliance. Snowball Edge adds **local compute**, so it can process data on site before or instead of shipping it anywhere. Edge devices also offer larger capacity and more network interfaces.",
        note:
          "AWS has since retired the original Snowball device, so in practice the Snow Family today means Snowball Edge.",
        table: {
          head: ["", "AWS Snowball (original)", "AWS Snowball Edge"],
          rows: [
            ["Purpose", "Data transfer only", "Transfer plus local processing"],
            ["Compute on board", "None", "EC2 instances and Lambda"],
            ["Configurations", "Single storage device", "Storage Optimized, Compute Optimized"],
            ["Typical capacity", "50 TB / 80 TB", "Larger, varies by configuration"],
            ["Status", "Retired", "Current"],
          ],
        },
      },
    ],
  },
];

/* =========================================================================
   Helpers
   ========================================================================= */

function Rich({ children }) {
  if (typeof children !== "string") return children || null;
  const out = [];
  const rx = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = rx.exec(children)) !== null) {
    if (m.index > last) out.push(children.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      out.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    } else {
      out.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < children.length) out.push(children.slice(last));
  return <>{out.map((p, i) => (typeof p === "string" ? <React.Fragment key={"s" + i}>{p}</React.Fragment> : p))}</>;
}

function DataTable({ head, rows }) {
  return (
    <div className="aws-table-wrap">
      <table className="aws-table">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>
                  <Rich>{c}</Rich>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* =========================================================================
   Question
   ========================================================================= */

function Question({ item, mn, qn, testMode, revealed, onReveal, done, onDone }) {
  const show = !testMode || revealed;
  return (
    <article className={"aws-q" + (done ? " aws-q-done" : "")} id={`m${mn}q${qn}`}>
      <div className="aws-q-head">
        <span className="aws-q-n">
          {mn}.{qn}
        </span>
        <h3>{item.q}</h3>
        <label className="aws-q-done-box" title="Mark as revised">
          <input type="checkbox" checked={done} onChange={onDone} />
        </label>
      </div>

      {item.term && <p className="aws-term">{item.term}</p>}

      {show ? (
        <div className="aws-a">
          <p>
            <Rich>{item.a}</Rich>
          </p>
          {item.note && (
            <p className="aws-note">
              <Rich>{item.note}</Rich>
            </p>
          )}
          {item.table && <DataTable head={item.table.head} rows={item.table.rows} />}
          {item.diagram && DIAGRAMS[item.diagram]}
        </div>
      ) : (
        <button type="button" className="aws-reveal" onClick={onReveal}>
          Show answer
        </button>
      )}
    </article>
  );
}

/* =========================================================================
   Page
   ========================================================================= */

const KEY = "aws-assignment:done:v1";

export default function AwsAssignment() {
  const [query, setQuery] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [revealed, setRevealed] = useState([]);
  const [done, setDone] = useState([]);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) setDone(JSON.parse(raw) || []);
    } catch {
      /* storage unavailable — the page still works */
    }
  }, []);

  const toggleDone = useCallback((id) => {
    setDone((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        window.localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const flipMode = () => {
    setTestMode((v) => !v);
    setRevealed([]);
  };

  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return MODULES;
    return MODULES.map((m) => ({
      ...m,
      questions: m.questions.filter((x) => (x.q + " " + x.a + " " + (x.term || "")).toLowerCase().includes(q)),
    })).filter((m) => m.questions.length > 0);
  }, [q]);

  const total = MODULES.reduce((n, m) => n + m.questions.length, 0);
  const pct = Math.round((done.length / total) * 100);

  return (
    <div className="aws-root">
      <style>{CSS}</style>

      <header className="aws-hero">
        <div className="aws-hero-in">
          <p className="aws-kicker">Basic assignment · beginner level</p>
          <h1>AWS Cloud Computing</h1>
          <p className="aws-lede">
            All 80 questions from the assignment, grouped by module, each with a model answer of the
            length the brief asks for. Comparison tables where a question asks for a difference, and
            diagrams for the topics that are much easier to see than to read.
          </p>
          <dl className="aws-stats">
            <div>
              <dt>Modules</dt>
              <dd>8</dd>
            </div>
            <div>
              <dt>Questions</dt>
              <dd>{total}</dd>
            </div>
            <div>
              <dt>Diagrams</dt>
              <dd>6</dd>
            </div>
          </dl>
        </div>
      </header>

      <div className="aws-toolbar">
        <div className="aws-toolbar-in">
          <input
            type="search"
            className="aws-search"
            placeholder="Search all 80 questions — try VPC, Multi-AZ, Spot"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search questions"
          />
          <button
            type="button"
            className={"aws-mode" + (testMode ? " aws-mode-on" : "")}
            onClick={flipMode}
            aria-pressed={testMode}
          >
            {testMode ? "Test mode" : "Study mode"}
          </button>
          <div className="aws-progress" title={`${done.length} of ${total} marked revised`}>
            <div className="aws-progress-bar">
              <span style={{ width: `${pct}%` }} />
            </div>
            <span className="aws-progress-n">
              {done.length}/{total}
            </span>
          </div>
          <button
            type="button"
            className="aws-navtoggle"
            onClick={() => setNavOpen((v) => !v)}
            aria-expanded={navOpen}
          >
            Modules
          </button>
        </div>
      </div>

      <div className="aws-layout">
        <nav className={"aws-toc" + (navOpen ? " aws-toc-open" : "")} aria-label="Modules">
          {MODULES.map((m) => (
            <a key={m.n} href={`#module-${m.n}`} onClick={() => setNavOpen(false)}>
              <span className="aws-toc-n">{m.n}</span>
              <span>
                {m.title}
                <em>CO {m.co}</em>
              </span>
            </a>
          ))}
        </nav>

        <main className="aws-main">
          {testMode && !q && (
            <p className="aws-hint">
              Test mode hides every answer. Read the question, answer it in your head or on paper,
              then reveal and compare.
            </p>
          )}

          {filtered.length === 0 && (
            <p className="aws-empty">Nothing matches "{query}". Try a shorter term.</p>
          )}

          {filtered.map((m) => (
            <section key={m.n} id={`module-${m.n}`} className="aws-module">
              <div className="aws-module-head">
                <span className="aws-module-n">{m.n}</span>
                <div>
                  <h2>{m.title}</h2>
                  <p>{m.blurb}</p>
                  <span className="aws-co">CO {m.co}</span>
                </div>
              </div>
              {m.questions.map((item) => {
                const qn = MODULES.find((x) => x.n === m.n).questions.indexOf(item) + 1;
                const id = `m${m.n}q${qn}`;
                return (
                  <Question
                    key={id}
                    item={item}
                    mn={m.n}
                    qn={qn}
                    testMode={testMode}
                    revealed={revealed.includes(id)}
                    onReveal={() => setRevealed((r) => [...r, id])}
                    done={done.includes(id)}
                    onDone={() => toggleDone(id)}
                  />
                );
              })}
            </section>
          ))}

          <footer className="aws-footer">
            <p>
              Answers follow the assignment instruction of roughly 2–5 sentences. Two services in the
              paper are no longer current — AWS SMS and the original Snowball — and both are flagged
              where they appear.
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}

/* =========================================================================
   Styles
   ========================================================================= */

const CSS = `
.aws-root {
  --a-bg: #f2f4f6;
  --a-surface: #ffffff;
  --a-surface-2: #e7eaef;
  --a-ink: #111826;
  --a-ink-2: #566179;
  --a-line: #d9dee5;
  --a-line-2: #c1c8d4;
  --a-accent: #9a5b06;
  --a-accent-bg: #fbeeda;
  --a-navy: #1d3557;
  --a-ok: #14683f;
  --a-ok-bg: #dcf0e4;
  --a-warn: #8a4a12;
  --a-warn-bg: #fae8d8;

  background: var(--a-bg);
  color: var(--a-ink);
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

[data-theme="dark"] .aws-root {
  --a-bg: #0b111c;
  --a-surface: #121a28;
  --a-surface-2: #1b2637;
  --a-ink: #e6ecf5;
  --a-ink-2: #9aa8bf;
  --a-line: #26334a;
  --a-line-2: #35455f;
  --a-accent: #f0a33f;
  --a-accent-bg: #2f2210;
  --a-navy: #9fc0e8;
  --a-ok: #62c795;
  --a-ok-bg: #11301f;
  --a-warn: #e6a86a;
  --a-warn-bg: #301f10;
}

@media (prefers-color-scheme: dark) {
  .aws-root:not([data-theme="light"] .aws-root) {
    --a-bg: #0b111c;
    --a-surface: #121a28;
    --a-surface-2: #1b2637;
    --a-ink: #e6ecf5;
    --a-ink-2: #9aa8bf;
    --a-line: #26334a;
    --a-line-2: #35455f;
    --a-accent: #f0a33f;
    --a-accent-bg: #2f2210;
    --a-navy: #9fc0e8;
    --a-ok: #62c795;
    --a-ok-bg: #11301f;
    --a-warn: #e6a86a;
    --a-warn-bg: #301f10;
  }
}

.aws-root *, .aws-root *::before, .aws-root *::after { box-sizing: border-box; }
.aws-root h1, .aws-root h2, .aws-root h3 { margin: 0; line-height: 1.2; letter-spacing: -0.02em; font-weight: 640; }
.aws-root p { margin: 0 0 0.8rem; }
.aws-root :focus-visible { outline: 2px solid var(--a-accent); outline-offset: 2px; }

/* hero */
.aws-hero { background: var(--a-surface); border-bottom: 1px solid var(--a-line); }
.aws-hero-in { width: 100%; padding: 3.6rem 1.5rem 2.8rem; box-sizing: border-box; }
.aws-kicker { color: var(--a-accent); font-size: 0.8rem; font-weight: 600; margin-bottom: 0.9rem; }
.aws-hero h1 { font-size: clamp(2.1rem, 6vw, 3.6rem); font-weight: 720; letter-spacing: -0.035em; }
.aws-lede { margin-top: 1.2rem; color: var(--a-ink-2); font-size: 1.02rem; }
.aws-stats { display: flex; flex-wrap: wrap; gap: 2.2rem; margin: 1.8rem 0 0; }
.aws-stats dt { font-size: 0.75rem; color: var(--a-ink-2); }
.aws-stats dd { margin: 0.1rem 0 0; font-size: 1.5rem; font-weight: 660; letter-spacing: -0.02em; }

/* toolbar */
.aws-toolbar { position: sticky; top: 0; z-index: 30; background: var(--a-bg); border-bottom: 1px solid var(--a-line); }
.aws-toolbar-in { width: 100%; padding: 0.55rem 1.5rem; display: flex; gap: 0.6rem; align-items: center; box-sizing: border-box; }
.aws-search {
  flex: 1 1 auto; min-width: 0; font: inherit; font-size: 0.87rem; padding: 0.42rem 0.7rem;
  border: 1px solid var(--a-line-2); border-radius: 5px; background: var(--a-surface); color: var(--a-ink);
}
.aws-mode {
  flex: none; font: inherit; font-size: 0.8rem; cursor: pointer; padding: 0.4rem 0.75rem;
  border: 1px solid var(--a-line-2); border-radius: 5px; background: var(--a-surface); color: var(--a-ink-2);
}
.aws-mode-on { background: var(--a-accent); border-color: var(--a-accent); color: #fff; font-weight: 600; }
[data-theme="dark"] .aws-mode-on { color: #0b111c; }
@media (prefers-color-scheme: dark) { .aws-mode-on { color: #0b111c; } }
.aws-progress { display: flex; align-items: center; gap: 0.45rem; flex: none; }
.aws-progress-bar { width: 74px; height: 5px; border-radius: 3px; background: var(--a-surface-2); overflow: hidden; }
.aws-progress-bar span { display: block; height: 100%; background: var(--a-ok); }
.aws-progress-n { font-size: 0.75rem; color: var(--a-ink-2); font-variant-numeric: tabular-nums; }
.aws-navtoggle {
  display: none; font: inherit; font-size: 0.8rem; cursor: pointer; padding: 0.4rem 0.7rem;
  border: 1px solid var(--a-line-2); border-radius: 5px; background: var(--a-surface); color: var(--a-ink);
}

/* layout */
.aws-layout { width: 100%; padding: 0 1.5rem; display: grid; grid-template-columns: minmax(0, 1fr); gap: 2rem; box-sizing: border-box; }
@media (min-width: 62rem) { .aws-layout { grid-template-columns: 15rem minmax(0, 1fr); gap: 3rem; } }

.aws-toc { display: none; }
@media (min-width: 62rem) {
  .aws-toc { display: block; position: sticky; top: 3.6rem; align-self: start; padding: 2rem 0; font-size: 0.85rem; }
}
.aws-toc.aws-toc-open { display: block; padding: 0.8rem 0 1.2rem; border-bottom: 1px solid var(--a-line); }
.aws-toc a {
  display: grid; grid-template-columns: 1.6rem 1fr; gap: 0.5rem; align-items: start;
  padding: 0.4rem 0; color: var(--a-ink); text-decoration: none; line-height: 1.3;
}
.aws-toc a:hover { color: var(--a-accent); }
.aws-toc-n { color: var(--a-accent); font-weight: 700; font-variant-numeric: tabular-nums; }
.aws-toc em { display: block; font-style: normal; font-size: 0.72rem; color: var(--a-ink-2); margin-top: 0.1rem; }

.aws-main { padding: 2.2rem 0 4rem; min-width: 0; width: 100%; }
.aws-hint {
  background: var(--a-accent-bg); border-radius: 5px; padding: 0.7rem 0.9rem;
  font-size: 0.88rem; margin-bottom: 1.8rem;
}
.aws-empty { color: var(--a-ink-2); padding: 2rem 0; }

/* module */
.aws-module { margin-bottom: 3.5rem; scroll-margin-top: 3.8rem; }
.aws-module-head {
  display: grid; grid-template-columns: auto 1fr; gap: 1rem; align-items: start;
  padding-bottom: 1.1rem; margin-bottom: 1.4rem; border-bottom: 2px solid var(--a-ink);
}
.aws-module-n {
  font-size: clamp(2rem, 5vw, 3rem); font-weight: 740; line-height: 0.8;
  color: var(--a-accent); letter-spacing: -0.04em;
}
.aws-module-head h2 { font-size: clamp(1.3rem, 3vw, 1.8rem); }
.aws-module-head p { margin: 0.45rem 0 0.5rem; color: var(--a-ink-2); font-size: 0.93rem; }
.aws-co {
  display: inline-block; font-size: 0.7rem; font-weight: 600; color: var(--a-navy);
  border: 1px solid var(--a-line-2); border-radius: 3px; padding: 0.08rem 0.4rem;
}

/* question */
.aws-q { border-top: 1px solid var(--a-line); padding: 1.15rem 0; scroll-margin-top: 3.8rem; }
.aws-q:last-of-type { border-bottom: 1px solid var(--a-line); }
.aws-q-done { opacity: 0.55; }
.aws-q-head { display: grid; grid-template-columns: 2.4rem 1fr auto; gap: 0.5rem; align-items: baseline; }
.aws-q-n { color: var(--a-accent); font-size: 0.8rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.aws-q-head h3 { font-size: 1.04rem; font-weight: 620; }
.aws-q-done-box input { accent-color: var(--a-ok); width: 1rem; height: 1rem; cursor: pointer; }

.aws-term {
  margin: 0.55rem 0 0 2.4rem; display: inline-block; font-size: 0.8rem; font-weight: 600;
  background: var(--a-accent-bg); color: var(--a-accent); border-radius: 3px; padding: 0.15rem 0.5rem;
}
.aws-a { margin: 0.6rem 0 0 2.4rem; width: calc(100% - 2.4rem); font-size: 0.95rem; box-sizing: border-box; }
.aws-a strong { font-weight: 640; }
.aws-note {
  background: var(--a-warn-bg); border-left: 3px solid var(--a-warn); border-radius: 0 4px 4px 0;
  padding: 0.55rem 0.8rem; font-size: 0.86rem; margin: 0.8rem 0;
}
.aws-reveal {
  margin: 0.6rem 0 0 2.4rem; font: inherit; font-size: 0.82rem; cursor: pointer;
  background: var(--a-surface); border: 1px dashed var(--a-line-2); color: var(--a-ink-2);
  border-radius: 5px; padding: 0.35rem 0.8rem;
}
.aws-reveal:hover { color: var(--a-accent); border-color: var(--a-accent); }

/* table */
.aws-table-wrap { overflow-x: auto; margin: 0.9rem 0 1.1rem; width: 100%; }
.aws-table { border-collapse: collapse; width: 100%; font-size: 0.85rem; min-width: 30rem; }
.aws-table th, .aws-table td {
  text-align: left; padding: 0.45rem 0.9rem 0.45rem 0; border-bottom: 1px solid var(--a-line); vertical-align: top;
}
.aws-table th { font-weight: 640; border-bottom-color: var(--a-line-2); white-space: nowrap; }
.aws-table td:first-child { font-weight: 500; }

/* diagram */
.aws-diagram { margin: 1.1rem 0 1.2rem; color: var(--a-ink-2); background: var(--a-surface); border: 1px solid var(--a-line); border-radius: 6px; padding: 0.9rem; width: 100%; }
.aws-diagram svg { width: 100%; height: auto; display: block; font-family: inherit; }
.aws-diagram-cap { margin: 0.7rem 0 0; font-size: 0.79rem; color: var(--a-ink-2); }
.aws-svg-box { fill: var(--a-surface); stroke: var(--a-line-2); }
.aws-svg-muted { fill: var(--a-surface-2); stroke: var(--a-line-2); }
.aws-svg-accent { fill: var(--a-accent-bg); stroke: var(--a-accent); }
.aws-svg-group { fill: none; stroke: var(--a-line-2); stroke-dasharray: 4 3; }
.aws-svg-t { fill: var(--a-ink); font-size: 12px; font-weight: 600; }
.aws-svg-t2 { fill: var(--a-ink-2); font-size: 10.5px; }
.aws-svg-line { stroke: currentColor; stroke-width: 1.4; }

.aws-footer { border-top: 1px solid var(--a-line); padding-top: 1.4rem; color: var(--a-ink-2); font-size: 0.85rem; }

@media (max-width: 61.99rem) { .aws-navtoggle { display: inline-block; } }
@media (prefers-reduced-motion: reduce) { .aws-root * { transition: none !important; } }

@media print {
  .aws-toolbar, .aws-toc, .aws-navtoggle, .aws-q-done-box, .aws-reveal { display: none !important; }
  .aws-root { background: #fff; font-size: 11pt; }
  .aws-layout { display: block; padding: 0; }
  .aws-module { break-before: page; }
  .aws-q { break-inside: avoid; }
}
`;
